import React from 'react';
import PropTypes from 'prop-types';
import { Tooltip, Spin, Icon } from 'antd';
import { autobind } from 'core-decorators';
import moment from 'moment';
import BaseComponent from 'components/BaseComponent';
import styledComp, { FlexRowContainer } from 'components/StyledComp';
import CustomButton from 'components/CustomButton';
import DataTables from 'components-desktop/DataTables';
import TfootSummary from 'components-desktop/TfootSummary';
import helpers from 'misc/helpers';

const propTypes = {
  title: PropTypes.string,
  patientInfos: PropTypes.array,
  adminFeeMinutesList: PropTypes.array,
  adminFeeInvoices: PropTypes.array,
  onInvoiceCreatedOrUpdated: PropTypes.func.isRequired,
};

const defaultProps = {
  title: '',
  patientInfos: [],
  adminFeeMinutesList: [],
  adminFeeInvoices: [],
};

@autobind
class TableAdminFeeInfos extends BaseComponent {
  constructor(props) {
    super(props);

    this.state = {
      exportingInvoiceId: null,
      exportingSelectedInvoices: false,

      workingPatientId: null, // The patient id whose invoice is being created/updated.
      workingSelectedPatients: false,
    };
  }

  async onPrintInvoiceClick(id) {
    try {
      this.setState({
        exportingInvoiceId: id,
      });

      const params = { ids: id };
      const responseBody = await this.callService('/invoice/export', params);

      // Open a window to show pdf.
      const blob = helpers.base64toBlob(responseBody.getRetBlob(), 'application/pdf');
      window.open(URL.createObjectURL(blob));
    } finally {
      this.setState({
        exportingInvoiceId: null,
      });
    }
  }

  async onPrintSelectedInvoicesClick() {
    const selectedInvoiceIds = this.dataTables.getSelectedRowKeys();
    if (selectedInvoiceIds.length > 0) {
      try {
        this.setState({
          exportingSelectedInvoices: true,
        });

        const params = { ids: selectedInvoiceIds };
        const responseBody = await this.callService('/invoice/export', params);

        // Open an window to show pdf.
        const blob = helpers.base64toBlob(responseBody.getRetBlob(), 'application/pdf');
        window.open(URL.createObjectURL(blob));
      } finally {
        this.setState({
          exportingSelectedInvoices: false,
        });
      }
    }
  }

  async onCreateOrUpdateInvoiceClick(patientId) {
    try {
      this.setState({
        workingPatientId: patientId,
      });

      const patientInfo = this.props.patientInfos.find(e => e.getLong('patientId') === patientId);
      const companyId = patientInfo.getLong('companyId');
      const month = moment(patientInfo.getLong('date')).format('YYYY-MM-05');

      const params = {
        patientId,
        companyId,
        month,
      };
      const invoice =
        await this.callServiceForRemoteObject('/invoice/create_admin_fee_invoice', params);

      if (invoice) {
        this.notifySuccess('Success', `An admin fee invoice has been created or updated.`);

        // Re-search.
        this.props.onInvoiceCreatedOrUpdated(invoice);
      }
    } finally {
      this.setState({
        workingPatientId: null,
      });
    }
  }

  async onCreateAllClick() {
    const selectedPatientIds = this.dataTables.getSelectedRowKeys();
    if (selectedPatientIds.length > 0) {
      try {
        this.setState({
          workingSelectedPatients: true,
        });

        const workedPatientIds = [];
        for (let i = 0; i < selectedPatientIds.length; i++) {
          const patientId = selectedPatientIds[i];

          // eslint-disable-next-line
          await this.onCreateOrUpdateInvoiceClick(patientId);

          // Will uncheck this row.
          workedPatientIds.push(patientId);
        }

        // Uncheck saved rows.
        const origSelections = this.dataTables.getSelectedRowKeys();
        this.dataTables.setSelectedRowKeys(
          origSelections.filter(e => !workedPatientIds.includes(e)));

        this.notifySuccess('Success', `${workedPatientIds.length} invoices created or updated.`);
      } finally {
        this.setState({
          workingSelectedPatients: false,
        });
      }
    } else {
      this.notifyWarning('No patient is selected.');
    }
  }

  createColumns() {
    const { exportingInvoiceId, workingPatientId } = this.state;

    return [{
      title: 'Patient',
      dataIndex: 'patientName',
      sorter: (a, b) => a.patientName?.localeCompare(b.patientName),
      defaultSortOrder: 'ascend',
      render: (text, record) => (
        <styledComp.A
          width="wrap_content"
          height="wrap_content"
          href={`/patients/patient_profile?id=${record.patientId}`}
          target="_blank"
        >
          {text}
        </styledComp.A>
      ),
    }, {
      title: 'Company',
      dataIndex: 'companyName',
      sorter: (a, b) => a.companyName?.localeCompare(b.companyName),
    }, {
      title: 'Month',
      dataIndex: 'month',
    }, {
      title: 'Hours',
      dataIndex: 'adminFeeHours',
      sorter: (a, b) => a.adminFeeMinutes - b.adminFeeMinutes,
    }, {
      title: 'Invoice Date',
      dataIndex: 'invoiceDateUsString',
      sorter: (a, b) => a.invoiceDate - b.invoiceDate,
    }, {
      title: 'Invoice Number',
      dataIndex: 'invoiceNumber',
      sorter: (a, b) => a.invoiceNumber?.localeCompare(b.invoiceNumber),
      render: (text, record) => (
        <styledComp.A
          width="wrap_content"
          height="wrap_content"
          href={`/invoices/invoice_details?id=${record.invoiceId}`}
          target="_blank"
        >
          {text}
        </styledComp.A>
      ),
    }, {
      title: 'Mail Date',
      dataIndex: 'mailDateUsString',
      sorter: (a, b) => a.mailDate - b.mailDate,
    }, {
      title: 'Total Amt.',
      dataIndex: 'amountInDollars',
      sorter: (a, b) => a.amount - b.amount,
    }, {
      title: 'Last Export',
      dataIndex: 'lastExportTimeUsString',
      sorter: (a, b) => a.lastExportTime - b.lastExportTime,
    }, {
      title: 'Remark',
      dataIndex: 'remark',
      sorter: (a, b) => a.remark?.localeCompare(b.remark),
      render: (text, record) => (
        <Tooltip title={text}>
          <styledComp.Span
            width="wrap_content"
            height="wrap_content"
            maxWidth="150px"
            color="textPrimaryDark"
            fontSize="12px"
            ellipsis
            display="inline-block"
          >
            {text}
          </styledComp.Span>
        </Tooltip>
      ),
    }, {
      title: 'Actions',
      render: (text, record) => (
        <FlexRowContainer
          width="match_parent"
          height="wrap_content"
          justifyContent="space-around"
        >
          {!record.invoiceId && workingPatientId !== record.patientId &&
            <styledComp.A
              width="wrap_content"
              height="wrap_content"
              fontSize="14px"
              onClick={() => this.onCreateOrUpdateInvoiceClick(record.patientId)}
            >
              Create
            </styledComp.A>
          }

          {record.invoiceId && workingPatientId !== record.patientId &&
            <styledComp.A
              width="wrap_content"
              height="wrap_content"
              fontSize="14px"
              onClick={() => this.onCreateOrUpdateInvoiceClick(record.patientId)}
            >
              Update
            </styledComp.A>
          }

          {workingPatientId === record.patientId &&
            <Spin />
          }

          {record.invoiceId && exportingInvoiceId !== record.invoiceId &&
            <styledComp.A
              width="wrap_content"
              height="wrap_content"
              fontSize="14px"
              onClick={() => this.onPrintInvoiceClick(record.invoiceId)}
            >
              Print
            </styledComp.A>
          }

          {record.invoiceId && exportingInvoiceId === record.invoiceId &&
            <Spin />
          }
        </FlexRowContainer>
      ),
    }];
  }

  createDataSource(patientInfos, adminFeeMinutesList, adminFeeInvoices) {
    const dataSource = [];
    patientInfos.forEach((patientInfo, index) => {
      const adminFeeMinutes = adminFeeMinutesList[index];
      const adminFeeInvoice = adminFeeInvoices[index];

      dataSource.push({
        key: patientInfo.getLong('patientId'),
        id: patientInfo.getLong('patientId'),
        patientId: patientInfo.getLong('patientId'),
        patientName: patientInfo.getString('patientName'),
        companyId: patientInfo.getLong('companyId'),
        companyName: patientInfo.getString('companyName'),
        date: patientInfo.getLong('date'),
        month: moment(patientInfo.getLong('date')).format('YYYY-MM'),

        adminFeeMinutes,
        adminFeeHours: helpers.min2ClockHour(adminFeeMinutes),

        invoiceId: adminFeeInvoice?.getId(),
        invoiceDate: adminFeeInvoice?.getLong('invoiceDate'),
        invoiceDateUsString: adminFeeInvoice?.getString('invoiceDateUsString'),
        invoiceNumber: adminFeeInvoice?.getString('invoiceNumber'),
        mailDate: adminFeeInvoice?.getLong('mailDate'),
        mailDateUsString: adminFeeInvoice?.getString('mailDateUsString'),
        lastExportTime: adminFeeInvoice?.getLong('lastExportTime'),
        lastExportTimeUsString: adminFeeInvoice?.getString('lastExportTimeUsString'),
        amount: adminFeeInvoice?.getDouble('amount'),
        amountInDollars: helpers.toDollars(adminFeeInvoice?.getDouble('amount')),
        remark: adminFeeInvoice?.getString('remark'),
      });
    });
    return dataSource;
  }

  renderTfoot(columnsToDisplay, dataRowsToDisplay) {
    return (
      <TfootSummary
        columnsToDisplay={columnsToDisplay}
        dataRowsToDisplay={dataRowsToDisplay}
        isTableSelectable
        columnKeysToAlign={['amountInDollars']}
        reducers={[
          TfootSummary.SUM_REDUCER_GENERATOR('amount')]
        }
        formatters={[
          n => helpers.toDollars(n),
        ]}
      />
    );
  }

  renderFooter() {
    const { exportingSelectedInvoices, workingSelectedPatients } = this.state;

    return (
      <FlexRowContainer
        width="match_parent"
        height="wrap_content"
        padding="20px 20px"
        justifyContent="center"
        backgroundColor="containerGrayBackground"
      >
        <CustomButton
          width="wrap_content"
          height="32px"
          margin="0 4px 0 0"
          backgroundColor="yellow_gold"
          fontSize="14px"
          type="primary"
          loading={workingSelectedPatients}
          onClick={this.onCreateAllClick}
        >
          CREATE SELECTED INVOICES
        </CustomButton>

        <CustomButton
          width="wrap_content"
          height="32px"
          backgroundColor="yellow_gold"
          fontSize="14px"
          type="primary"
          loading={exportingSelectedInvoices}
          onClick={this.onPrintSelectedInvoicesClick}
        >
          PRINT SELECTED INVOICES
        </CustomButton>
      </FlexRowContainer>
    );
  }

  renderContent() {
    const { patientInfos, adminFeeMinutesList, adminFeeInvoices, title,
      ...layoutProps } = this.props;

    return (
      <DataTables
        {...layoutProps}
        ref={(ref) => { this.dataTables = ref; }}
        color="yellow_gold"
        fontSize="12px"
        size="small"
        title={title}
        icon="file-text"
        selectable
        columns={this.createColumns()}
        dataSource={this.createDataSource(patientInfos, adminFeeMinutesList, adminFeeInvoices)}
        tfoot={this.renderTfoot}
        footer={this.renderFooter()}
      />
    );
  }
}

// https://facebook.github.io/react/docs/typechecking-with-proptypes.html
TableAdminFeeInfos.propTypes = propTypes;
TableAdminFeeInfos.defaultProps = defaultProps;

export default TableAdminFeeInfos;
